import React from "react";
import { EMPTY, eventName, toNum } from "../../../../utils/perfFormat";
import { usePhoneTheme } from "../../useIsPhone";
import { TapPanel, openIndexIn, tapScopeOf, useTapPanel } from "../TapPanel";
import ChartTargets, { columnPlacement } from "./ChartTargets";
import { CHART, FONT, ROLE } from "./chartTokens";

// Game by game bars: one bar per game for the picked stat, coloured by the
// team's result (bm-orange for a win, grey for a loss; a camp game hollow
// on desktop and bm-orange/40 on phones; a game with no final result, an
// unscored game, hollow grey, never drawn as a win; a game the player
// missed reads "DNP" with no bar), the value over each bar, the date under
// it, the events as dashed separators with their names, the rolling
// average as an ink line and the season average as a dashed ink line.
// gameTone(game) names a bar's treatment ("win", "loss", "camp", "none")
// so a card can add the legend entry for the ones it shows.
//
//   GameBars({ games, format, average, averageLabel, rangeLabel, yStep, yUnit,
//              ariaLabel, renderDetail, listLabel, caption, phone, activeIndex })
//
// games   [{ key, value, label, result, event, camp, dnp, rolling, ariaLabel }]
//         `label` is the short date under the bar ("05/15" or "May 15"),
//         `result` the team's "W" / "L" / "T", `event` the event name (its
//         parenthetical place is dropped), `rolling` the rolling average at
//         that game, `ariaLabel` the game in words ("May 15 vs Team A, win
//         70-65: 23 points").
// format(v) the value labels (default: whole numbers as they are, else one
//         decimal). yStep / yUnit fix the axis (20 and "%" for TS%); a
//         yStep that is not a positive number is ignored.
// averageLabel ("season avg 19") and rangeLabel ("May 15 – Jul 18") are
//         printed on the desktop chart.
// renderDetail(i) is the game's detail: a HoverCard on hover or focus of
//         its column (desktop), the panel under the bars after a tap (phone).
//         Without it the chart is static: no column targets on desktop, and
//         on phones each bar is a named mark (role="img"), not a button.
// Desktop: a 960 x 300 SVG (role="img", `ariaLabel`) that scales with the
//   card; value labels drop when the bars get narrow, dates thin out.
// Phones: a row of bar buttons that scrolls sideways (grouped by event),
//   values over the bars, the averages as a dotted rolling marker and a
//   dashed season line on each bar, and `caption` under the row ("Rolling
//   5-game avg 15.8 · Season avg 19.0").

const cx = (...parts) => parts.filter(Boolean).join(" ");

const W = 960;
const H = 300;
const L = 40;
const R = 12;
const T = 34;
const B = 42;
const PHONE_AREA = 108;
const PHONE_BAR_MAX = 88;

export const defaultFormat = (v) => {
  const n = toNum(v);
  if (n === null) return EMPTY;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

// The axis step for a maximum (the mock's steps, one more for big numbers).
export const autoStep = (max) => (max > 60 ? 20 : max > 25 ? 10 : max > 10 ? 5 : max > 4 ? 2 : 1);

export const gameScale = (games, { yStep } = {}) => {
  const values = games.flatMap((g) => [g.dnp ? null : toNum(g.value), toNum(g.rolling)]).filter((v) => v !== null);
  const max = Math.max(0, ...values);
  // Only a positive number may fix the step: 0, a negative or a string
  // would stall or garble the gridline loop.
  const fixedStep = toNum(yStep);
  const step = fixedStep !== null && fixedStep > 0 ? fixedStep : autoStep(max);
  const hi = Math.max(step, Math.ceil(max / step) * step);
  return { step, hi };
};

// Consecutive games of one event, in order.
export const eventGroups = (games) => {
  const groups = [];
  games.forEach((game, i) => {
    const name = eventName(game.event);
    const last = groups[groups.length - 1];
    if (last && last.name === name) last.items.push({ game, i });
    else groups.push({ name, start: i, items: [{ game, i }] });
  });
  return groups;
};

// A bar's treatment: a camp game (on the timeline, never scored), else the
// team's result. Only a "W" is a win: a game with no final result (null,
// a tie, anything else) is "none", never painted as a win or a loss.
export const gameTone = (game) => {
  if (game?.camp) return "camp";
  if (game?.result === "W") return "win";
  if (game?.result === "L") return "loss";
  return "none";
};

// Desktop bar paint and phone bar classes per tone.
const BAR_PAINT = {
  camp: { fill: CHART.white, stroke: CHART.orange, strokeWidth: 1.5 },
  win: { fill: ROLE.win },
  loss: { fill: ROLE.loss },
  none: { fill: CHART.white, stroke: CHART.tertiary, strokeWidth: 1.5 },
};
const PHONE_BAR = {
  camp: "bg-bm-orange/40",
  win: "bg-bm-orange",
  loss: "bg-bm-tertiary",
  none: "border-[1.5px] border-bm-tertiary bg-white",
};
const barFill = (game) => BAR_PAINT[gameTone(game)];

const truncate = (text, maxChars) => {
  if (maxChars < 3) return "";
  return text.length > maxChars ? `${text.slice(0, Math.max(1, maxChars - 1))}…` : text;
};

const DesktopGameBars = ({ games, format, average, averageLabel, rangeLabel, yStep, yUnit, ariaLabel, renderDetail, listLabel, activeIndex }) => {
  const n = games.length;
  const bw = (W - L - R) / n;
  const { step, hi } = gameScale(games, { yStep });
  const Y = (v) => T + ((hi - Math.min(hi, Math.max(0, v))) / hi) * (H - T - B);
  const X = (i) => L + i * bw + bw / 2;
  const showValues = bw >= 22;
  const dateEvery = Math.max(1, Math.ceil(36 / bw));
  const groups = eventGroups(games);
  const avg = toNum(average);

  const grid = [];
  for (let g = 0; g <= hi + 1e-9; g += step) grid.push(Math.round(g * 1000) / 1000);

  // The rolling line, broken where a game has no rolling value.
  const segments = [];
  let current = [];
  games.forEach((game, i) => {
    const r = toNum(game.rolling);
    if (r === null) {
      if (current.length) segments.push(current);
      current = [];
    } else current.push([X(i), Y(r)]);
  });
  if (current.length) segments.push(current);
  const lastRolling = [...games.keys()].reverse().find((i) => toNum(games[i].rolling) !== null);

  const targets = games.map((game, i) => ({
    key: game.key ?? i,
    label: game.ariaLabel || `${game.label}: ${game.dnp ? "DNP" : format(game.value)}`,
    placement: columnPlacement(i, n, "top"),
    style: {
      left: `${((L + i * bw) / W) * 100}%`,
      width: `${(bw / W) * 100}%`,
      top: `${((T - 6) / H) * 100}%`,
      height: `${((H - B - T + 6) / H) * 100}%`,
    },
  }));

  return (
    <div className="relative w-full">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel} className="block h-auto w-full">
        {grid.map((g) => (
          <g key={`grid-${g}`}>
            <line x1={L} x2={W - R} y1={Y(g)} y2={Y(g)} stroke={g === 0 ? CHART.border : CHART.mutedbg} strokeWidth={1} />
            <text x={L - 7} y={Y(g) + 4} textAnchor="end" fontFamily={FONT.body} fontSize={11} fill={CHART.muted}>
              {`${g}${yUnit || ""}`}
            </text>
          </g>
        ))}
        {groups.map((group, j) => {
          const x0 = L + group.start * bw;
          const span = group.items.length * bw;
          return (
            <g key={`event-${group.start}`}>
              {j > 0 && <line x1={x0} x2={x0} y1={T - 20} y2={H - B} stroke={CHART.border} strokeDasharray="3 4" />}
              {group.name && (
                <text x={x0 + 5} y={T - 14} fontFamily={FONT.body} fontSize={11.5} fontWeight={600} fill={CHART.muted}>
                  {truncate(group.name, Math.floor((span - 8) / 6.4))}
                </text>
              )}
            </g>
          );
        })}
        {games.map((game, i) => {
          const v = toNum(game.value);
          // Every dateEvery-th date, never one crowding the last, always the last.
          const label = i === n - 1 || (i % dateEvery === 0 && n - 1 - i >= dateEvery) ? game.label : null;
          const dateText = label ? (
            <text x={X(i)} y={H - B + 17} textAnchor="middle" fontFamily={FONT.body} fontSize={11} fill={activeIndex === i ? CHART.ink : CHART.muted}>
              {label}
            </text>
          ) : null;
          if (game.dnp || v === null) {
            return (
              <g key={`bar-${game.key ?? i}`}>
                {showValues && (
                  <text x={X(i)} y={Y(0) - 6} textAnchor="middle" fontFamily={FONT.body} fontSize={10} fontWeight={600} fill={CHART.muted}>
                    {game.dnp ? "DNP" : EMPTY}
                  </text>
                )}
                {dateText}
              </g>
            );
          }
          const y = Y(v);
          const width = bw * 0.64;
          return (
            <g key={`bar-${game.key ?? i}`}>
              <rect x={X(i) - width / 2} y={y} width={width} height={Math.max(1.5, Y(0) - y)} rx={Math.min(4, width / 2)} {...barFill(game)} />
              {showValues && (
                <text x={X(i)} y={y - 6} textAnchor="middle" fontFamily={FONT.num} fontSize={11} fontWeight={700} fill={CHART.ink}>
                  {format(v)}
                </text>
              )}
              {dateText}
            </g>
          );
        })}
        {avg !== null && (
          <g>
            <line x1={L} x2={W - R} y1={Y(avg)} y2={Y(avg)} stroke={ROLE.average} strokeOpacity={0.6} strokeWidth={1.5} strokeDasharray="6 5" />
            {averageLabel && (
              <text x={W - R} y={Y(avg) - 6} textAnchor="end" fontFamily={FONT.body} fontSize={11} fill={CHART.muted}>
                {averageLabel}
              </text>
            )}
          </g>
        )}
        {segments.map((seg, k) =>
          seg.length > 1 ? (
            <path
              key={`roll-${k}`}
              d={seg.map(([x, y], j) => `${j ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ")}
              fill="none"
              stroke={ROLE.trend}
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ) : null,
        )}
        {lastRolling !== undefined && <circle cx={X(lastRolling)} cy={Y(toNum(games[lastRolling].rolling))} r={4.5} fill={ROLE.trend} />}
        {rangeLabel && (
          <text x={L} y={H - 6} fontFamily={FONT.body} fontSize={11} fill={CHART.muted}>
            {rangeLabel}
          </text>
        )}
      </svg>
      {renderDetail && <ChartTargets targets={targets} renderDetail={renderDetail} label={listLabel} activeIndex={activeIndex} />}
    </div>
  );
};

const PhoneGameBars = ({ games, format, average, yStep, renderDetail, listLabel, caption, tap }) => {
  const { hi } = gameScale(games, { yStep });
  const px = (v) => Math.max(0, Math.min(1, v / hi)) * PHONE_BAR_MAX;
  const avg = toNum(average);
  const groups = eventGroups(games);
  const named = groups.some((g) => g.name);
  const openIndex = openIndexIn(tap, games.length);

  return (
    <div className="flex flex-col">
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
      <ul role="list" aria-label={listLabel} className="flex items-end gap-3 overflow-x-auto pb-1 bm-no-scrollbar">
        {groups.map((group) => (
          <li key={`group-${group.start}`} className="flex shrink-0 flex-col gap-1">
            {named && (
              <span aria-hidden="true" className="block max-w-[200px] truncate bm-body-xs text-bm-muted">
                {group.name || EMPTY}
              </span>
            )}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
            <ul role="list" className={cx("flex items-start gap-1", named && "border-l border-dashed border-bm-border pl-1")}>
              {group.items.map(({ game, i }) => {
                const v = toNum(game.value);
                const blank = game.dnp || v === null;
                const h = blank ? 0 : Math.max(2, px(v));
                const r = toNum(game.rolling);
                // A bar opens its game only when there is a detail to show;
                // a static chart's bars are named marks, not buttons that do
                // nothing (as the desktop chart has no targets then).
                const Bar = renderDetail ? "button" : "span";
                const barProps = renderDetail ? tap.triggerProps(i) : { role: "img" };
                return (
                  <li key={`game-${game.key ?? i}`} className="flex">
                    <Bar
                      {...barProps}
                      aria-label={game.ariaLabel || `${game.label}: ${game.dnp ? "DNP" : format(game.value)}`}
                      className={cx(
                        "flex w-7 flex-col items-center gap-1 rounded-[8px] pb-1 pt-1",
                        renderDetail &&
                          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-bm-ink",
                        openIndex === i ? "bg-bm-mutedbg" : renderDetail && "hover:bg-bm-subtle",
                      )}
                    >
                      <span aria-hidden="true" className="relative block w-full" style={{ height: PHONE_AREA }}>
                        {!blank && (
                          <span
                            data-tone={gameTone(game)}
                            className={cx("absolute bottom-0 left-1/2 w-3 -translate-x-1/2 rounded-t-[3px]", PHONE_BAR[gameTone(game)])}
                            style={{ height: h }}
                          />
                        )}
                        <span
                          className={cx("absolute left-1/2 -translate-x-1/2 whitespace-nowrap", blank ? "bm-label-xs text-bm-muted" : "font-archivo text-[10px] font-bold leading-[13px] text-bm-ink")}
                          style={{ bottom: h + 2 }}
                        >
                          {game.dnp ? "DNP" : blank ? EMPTY : format(v)}
                        </span>
                        {avg !== null && <span className="absolute inset-x-0 border-t border-dashed border-bm-ink/50" style={{ bottom: px(avg) }} />}
                        {r !== null && <span className="absolute left-1/2 h-[6px] w-[6px] -translate-x-1/2 rounded-full bg-bm-ink" style={{ bottom: px(r) - 3 }} />}
                      </span>
                      <span aria-hidden="true" className={cx("text-center bm-body-xs", openIndex === i ? "text-bm-ink" : "text-bm-muted")}>
                        {game.label}
                      </span>
                    </Bar>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
      {caption && <p className="pt-2 bm-body-xs text-bm-muted">{caption}</p>}
      {renderDetail && (
        <TapPanel id={tap.panelId} open={openIndex !== null} onClose={tap.close}>
          {openIndex !== null ? renderDetail(openIndex) : null}
        </TapPanel>
      )}
    </div>
  );
};

export const GameBars = ({
  games = [],
  format = defaultFormat,
  average = null,
  averageLabel,
  rangeLabel,
  yStep,
  yUnit = "",
  ariaLabel,
  renderDetail,
  listLabel = "Games",
  caption,
  phone: phoneProp,
  activeIndex = null,
}) => {
  const themePhone = usePhoneTheme();
  const phone = phoneProp ?? themePhone;
  // A new list of games (another season) closes the panel.
  const tap = useTapPanel(tapScopeOf(games));
  if (!games.length) return null;
  if (phone) {
    return (
      <PhoneGameBars games={games} format={format} average={average} yStep={yStep} renderDetail={renderDetail} listLabel={listLabel} caption={caption} tap={tap} />
    );
  }
  return (
    <DesktopGameBars
      games={games}
      format={format}
      average={average}
      averageLabel={averageLabel}
      rangeLabel={rangeLabel}
      yStep={yStep}
      yUnit={yUnit}
      ariaLabel={ariaLabel || listLabel}
      renderDetail={renderDetail}
      listLabel={listLabel}
      activeIndex={activeIndex}
    />
  );
};

export default GameBars;
